import type { ReactNode } from "react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { Deal } from "@/types/deal";
import { formatDate, formatDateForExport } from "@/utils/formatDate";
import {
  OutlookCell,
  StackedCell,
  type StackedLines,
} from "./components/StackedCell";
import { StackedCardEditor } from "./components/StackedCardEditor";

/**
 * Two-line columns for the Meeting view, so the meeting's fields fit in eight
 * columns. In edit mode, double-clicking one opens a card with an input for
 * each value in it.
 */

type Row = Record<string, unknown>;

const text = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) return value.map(text).filter(Boolean).join(", ");
  if (typeof value === "object") {
    const obj = value as Row;
    for (const key of [
      "sponsor_name",
      "source_name",
      "nicename",
      "sector",
      "name",
    ]) {
      if (typeof obj[key] === "string" && obj[key]) return obj[key] as string;
    }
    return "";
  }
  return String(value);
};

const pct = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  return Number.isFinite(n) ? `${+n.toFixed(2)}%` : String(value);
};

const years = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  return Number.isFinite(n) ? `${+n.toFixed(1)}y` : String(value);
};

export function meetingProfileLines(row: Row): StackedLines {
  const geo = [text(row.countries), text(row.sectors)].filter(Boolean);
  return {
    top: text(row.sponsor),
    bottom: geo.join(" · "),
  };
}

const amount = (value: unknown, currency: unknown): string => {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const code = typeof currency === "string" && currency ? ` ${currency}` : "";
  return `${n.toLocaleString("en-GB")}${code}`;
};

export function meetingAmountsLines(row: Row): StackedLines {
  return {
    top: amount(row.loan_amount_fully_funded, row.loan_amount_currency),
    bottom: `Day 1 ${amount(row.loan_amount_day_one, row.loan_amount_currency)}`,
  };
}

export function meetingTermsLines(row: Row): StackedLines {
  return {
    top: `Margin ${pct(row.margin)} · Fee ${pct(row.arrangement_fee)}`,
    bottom: `LTV ${pct(row.ltv)} · LTC ${pct(row.ltc)}`,
  };
}

export function meetingDatesLines(
  row: Row,
  formatDay: (value: string) => string = formatDate,
): StackedLines {
  const day = (value: unknown): string =>
    typeof value === "string" && value ? formatDay(value) : "–";
  return {
    top: `IC ${day(row.proposed_ic_date)}`,
    bottom: `Close ${day(row.target_closing)}`,
  };
}

/** CSV text for the IC / Closing cell; exports keep dd/MM/yyyy dates. */
function meetingDatesExport(row: Row | undefined): string {
  if (!row) return "";
  const lines = meetingDatesLines(row, formatDateForExport);
  return `${lines.top} | ${lines.bottom}`;
}

export function meetingOutlookLines(row: Row): StackedLines {
  const probability =
    typeof row.probability === "number" ? `${row.probability}%` : "–";
  return {
    top: probability,
    bottom:
      typeof row.priority === "string" && row.priority ? row.priority : "–",
  };
}

export function meetingTeamLines(row: Row): StackedLines {
  return {
    top: text(row.deal_lead) || "–",
    bottom: text(row.deal_support) || "–",
  };
}

export function meetingReturnsLines(row: Row): StackedLines {
  return {
    top: `IRR ${pct(row.irr_local)} · net ${pct(row.irr_local_net)}`,
    bottom: `USD ${pct(row.irr_usd)} · WAL ${years(row.wal)}`,
  };
}

/** Text the CSV export uses for this column (see csvExportCell). */
function withExportValue(
  col: ColDef<Deal>,
  exportValue: (row: Row | undefined) => string,
): ColDef<Deal> {
  return { ...col, context: { ...col.context, exportValue } };
}

function stackedColumn(
  colId: string,
  headerName: string,
  width: number,
  toLines: (row: Row) => StackedLines,
  /** Fields shown in the cell: a change to any flashes this cell, and the
   *  edit card has one input per field. */
  fields: string[],
  editMode = false,
  /** Custom content instead of two lines of text. */
  render?: (row: Row) => ReactNode,
): ColDef<Deal> {
  return {
    colId,
    headerName,
    width,
    editable: editMode
      ? (params) =>
          fields.some((field) =>
            params.api.getColumn(field)?.isCellEditable(params.node),
          )
      : false,
    cellEditor: StackedCardEditor,
    cellEditorParams: { editFields: fields },
    cellEditorPopup: true,
    cellEditorPopupPosition: "under",
    sortable: false,
    filter: false,
    valueGetter: (params) => {
      if (!params.data) return "";
      const lines = toLines(params.data as unknown as Row);
      return `${lines.top} | ${lines.bottom}`;
    },
    cellRenderer: (params: ICellRendererParams<Deal>) => {
      if (!params.data) return null;
      const row = params.data as unknown as Row;
      return render ? render(row) : <StackedCell lines={toLines(row)} />;
    },
    context: { sourceFields: fields },
  };
}

export function buildMeetingColumns(editMode = false): ColDef<Deal>[] {
  return [
    stackedColumn(
      "meeting_profile",
      "Sponsor / Geography",
      230,
      meetingProfileLines,
      ["sponsor", "countries", "sectors"],
      editMode,
    ),
    stackedColumn(
      "meeting_amounts",
      "Loan Amount FF / Day 1",
      150,
      meetingAmountsLines,
      ["loan_amount_fully_funded", "loan_amount_day_one"],
      editMode,
    ),
    stackedColumn(
      "meeting_terms",
      "Pricing / Leverage",
      150,
      meetingTermsLines,
      ["margin", "arrangement_fee", "ltv", "ltc"],
      editMode,
    ),
    stackedColumn(
      "meeting_returns",
      "Returns",
      150,
      meetingReturnsLines,
      ["irr_local", "irr_local_net", "irr_usd", "wal"],
      editMode,
    ),
    withExportValue(
      stackedColumn(
        "meeting_dates",
        "IC / Closing",
        130,
        (row) => meetingDatesLines(row),
        ["proposed_ic_date", "target_closing"],
        editMode,
      ),
      meetingDatesExport,
    ),
    stackedColumn(
      "meeting_outlook",
      "Probability / Priority",
      120,
      meetingOutlookLines,
      ["probability", "priority"],
      editMode,
      (row) => (
        <OutlookCell probability={row.probability} priority={row.priority} />
      ),
    ),
    stackedColumn(
      "meeting_team",
      "Deal Team",
      150,
      meetingTeamLines,
      ["deal_lead", "deal_support"],
      editMode,
    ),
  ];
}

/** Stacked column ids whose content depends on ``field`` (for live flashes). */
export function meetingColumnsForField(field: string): string[] {
  return buildMeetingColumns()
    .filter((col) =>
      (
        col.context as { sourceFields?: string[] } | undefined
      )?.sourceFields?.includes(field),
    )
    .map((col) => col.colId!);
}
